<template lang='pug'>
.asset-table

	// ! Move to AssetTable
	table-settings(
		:name='assetName'
		:columns='columns'
		:state='tableSettingsState'

		@settings='tableSettings = $event'
		@close='tableSettingsState = "close"'
	)

	phantom-api-table(
		:columns='columns'
		:get-all='getRows'
		:get='get'
		:search='searchRows'
		:setup-params='setupParams'
		:updated='updated'
		:deleted='deleted'

		@get-params='getParams = $event'
	)
		div.slot-wrapper(slot-scope="{ rows, filters, filteredRows, state, isFiltered, length, fetch, sort, search, updateOffset, updateIncrement, addFilter, clearFilter }")
			paginated-table-view(
				:columns='columns'
				:rows='rows'
				:data-state='state'
				:length='length'
				:is-filtered='isFiltered'
				:increment='15'
				:class="`nav-state-${$store.state.navState}`"

				@fetch='fetch'
				@sort='sort'
				@offset='updateOffset'
				@increment='updateIncrement'
				@clear-filter='clearFilter'
				@row-clicked='( a, b ) => emitEvent( "row-clicked", b )'
				@cell-clicked='( a, b ) => emitEvent( "cell-clicked", a, b )'
			)
				asset-search(
					slot='search'

					:placeholder='searchText'
					state='open'

					@search='search'
					@csv='downloadCSV'
					@pdf='downloadPDF'
					@settings='tableSettingsState = "open"'
				)

				div(v-for='( col, key ) in columns' :slot='`${key}Filter`')
					string-filter(
						v-if='col.filter === "string"'
						@filter='addFilter( col, $event )'
						@open='openFilter = key'
						:open-filter='openFilter === col.key'
					)
					dropdown-filter(
						v-else-if='col.filter === "dropdown"'
						:options='config.dropdownOptions[key]'
						@filter='addFilter( col, $event )'
						@open='openFilter = key'
						:open-filter='openFilter === col.key'
					)
					date-filter(
						v-else-if='col.filter === "date"'
						@filter='addFilter( col, $event )'
						@open='openFilter = key'
						:open-filter='openFilter === col.key'
					)
					boolean-filter(
						v-else-if='col.filter === "boolean"'
						@filter='addFilter( col, $event )'
					)

	new-filters-modal(
		:state='newFiltersState'
		:config='config'

		@close='closeNewFilters'
		@download='downloadWithNewFilters'
	)

</template>

<script>
import PaginatedTableView from '@/components/PaginatedTableView';
import PhantomApiTable from '@/components/PhantomApiTable';
import SimpleSearch from '@/components/SimpleSearch';
import StringFilter from '@/components/StringFilter';
import BooleanFilter from '@/components/BooleanFilter';
import DateFilter from '@/components/DateFilter';
import DropdownFilter from '@/components/DropdownFilter';
import AssetSearch from '@/components/AssetSearch';
import NewFiltersModal from '@/components/modal/NewFiltersModal';
import TableSettings from '@/components/TableSettings';

import {
	GetLastUpdated,
	GetLastDeleted,
} from '@/lib/db/recentlyViewed';

import {
	FilterToAPI,
	SortToAPI,
	clone,
} from '@/lib/utils';

export default {
	name : 'asset-table',

	props : {
		assetName : {
			type     : String,
			required : true,
		},

		assetKey : {
			type     : String,
			required : true,
		},

		config : {
			type     : Object,
			required : true,
		},

		downloadCsv : {
			type     : Function,
			required : true,
		},

		downloadPdf : {
			type     : Function,
			required : true,
		},

		getAll : {
			type : Function
		},

		get : {
			type : Function
		},

		search : {
			type : Function
		},

		searchText : {
			type : String,
		},
	},

	created() {

		// ! Move to AssetTable.vue
		GetLastUpdated( this.assetName, ( updated ) => {

			// on initial render, don't try to get the last
			// updated asset. It's pointless to do so since
			// we are already pulling all of the assets.
			if ( this.timesUpdated > 0 ) {
				this.updated = updated;
			}

			this.timesUpdated += 1;

		} );

		GetLastDeleted( this.assetName, ( deleted ) => {

			// on initial render, don't try to get the last
			// deleted asset. It's pointless to do so since
			// we are already pulling all of the assets.
			if ( this.timesDeleted > 0 ) {
				// console.log( deleted );
				this.deleted = deleted;
			}

			this.timesDeleted += 1;

		} );

	},

	data : () => ( {
		// + Filter Settings
		newFiltersState : 'closed',
		fileType        : '',

		openFilter : '',

		updated      : null,
		timesUpdated : 0,

		deleted      : null,
		timesDeleted : 0,

		// + Table Settings
		tableSettings      : {},
		tableSettingsState : 'close',

		getParams : null,
	} ),

	computed : {

		columns() {
			const columns           = clone( this.config.columns );
			const { tableSettings } = this;

			return Object.keys( columns ).reduce( ( obj, key ) => {
				const column     = clone( columns[key] );
				const setting    = tableSettings[key] || { hidden : false };
				const { hidden } = setting;

				obj[key] = { // eslint-disable-line
					key,
					...column,
					hidden,
				};

				return obj;
			}, {} );
		},

	},

	methods : {

		emitEvent( event, arg ) {
			this.$emit( event, arg );
		},

		getRows( offset, increment, getParams ) {
			const { filters, columns } = getParams;

			return this.getAll( filters, columns, offset, offset + increment );
		},

		searchRows( query, getParams ) {
			const { filters, columns } = getParams;
			return this.search( query, filters, columns );
		},

		// ! Move to AssetTable
		async downloadCSV( filterType ) {
			const { getParams } = this;
			const { filters, columns } = getParams;

			if ( filterType === 'new' ) {
				this.openNewFilters( 'csv' );
				return;
			}

			const stateIndicator = await this.$store.dispatch( 'displayState', {
				message : 'Downloading CSV...',
				state   : 'loading'
			} );

			if ( filterType === 'all' ) {

				this.downloadCsv( [], columns )
					.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
					.catch( ( e ) => {
						console.error( e );

						stateIndicator.fail( { header : 'Didn\'t Download CSV', message : '' } );
					} );

				return;
			}

			this.downloadCsv( filters, columns )
				.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
				.catch( ( e ) => {
					console.error( e );

					stateIndicator.fail( { header : 'Didn\'t Download CSV', message : '' } );
				} );
		},

		// ! Move to AssetTable
		async downloadPDF( filterType ) {
			const { getParams } = this;
			const { filters, columns } = getParams;

			if ( filterType === 'new' ) {
				this.openNewFilters( 'pdf' );

				return;
			}

			const stateIndicator = await this.$store.dispatch( 'displayState', {
				message : 'Downloading PDF...',
				state   : 'loading'
			} );

			if ( filterType === 'all' ) {
				this.downloadPdf( [], columns )
					.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
					.catch( ( e ) => {
						console.error( e );

						stateIndicator.fail( { header : 'Didn\'t Download PDF', message : '' } );
					} );
				return;
			}

			this.downloadPdf( filters, columns )
				.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
				.catch( ( e ) => {
					console.error( e );

					stateIndicator.fail( { header : 'Didn\'t Download PDF', message : '' } );
				} );
		},

		// ! Move to AssetTable.vue
		setupParams( filters, columns = {}, sortedColumn ) {
			const formattedFilters = FilterToAPI( filters );
			const formattedSort    = SortToAPI( sortedColumn );

			// eslint-disable-next-line no-unused-vars
			const { assetKey } = this;

			return {
				filters : formattedSort ? [formattedSort].concat( formattedFilters ) : formattedFilters,
				columns : Object.keys( columns )
					.filter( a => !columns[a].hidden )
					.map( a => columns[a].filterColumn || a )
			};
		},

		openNewFilters( fileType, filterOption ) {
			this.newFiltersState = 'opened';

			this.fileType     = fileType;
			this.filterOption = filterOption;
		},

		closeNewFilters() {
			this.newFiltersState = 'closed';

			this.fileType     = '';
			this.filterOption = '';
		},

		async downloadWithNewFilters( filters ) {
			const { fileType, getParams } = this;
			const { columns } = getParams;
			const formattedFilters = FilterToAPI( filters );

			let stateIndicator;

			switch ( fileType ) {
				case 'csv':

					stateIndicator = await this.$store.dispatch( 'displayState', {
						message : 'Downloading CSV...',
						state   : 'loading'
					} );

					this.downloadCsv( formattedFilters, columns )
						.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
						.catch( ( e ) => {
							console.error( e );

							stateIndicator.fail( { header : 'Didn\'t Download CSV', message : '' } );
						} );

					break;

				case 'pdf':

					stateIndicator = await this.$store.dispatch( 'displayState', {
						message : 'Downloading PDF...',
						state   : 'loading'
					} );

					this.downloadPdf( formattedFilters, columns )
						.then( () => stateIndicator.resolve( 'Downloaded Successfully' ) )
						.catch( ( e ) => {
							console.error( e );

							stateIndicator.fail( { header : 'Didn\'t Download PDF', message : '' } );
						} );

					break;

				default:
					break;
			}
		},
	},

	components : {
		PaginatedTableView,
		PhantomApiTable,
		SimpleSearch,
		StringFilter,
		BooleanFilter,
		DropdownFilter,
		AssetSearch,
		NewFiltersModal,
		DateFilter,
		TableSettings,
	}
};
</script>

<style lang='scss'>

.asset-table {

	.table-wrapper {
		min-height: 300px;
	}

	.slot-wrapper {

		.space-table {

			&.nav-state-collapsed {

				.pagination {
					left: 80px;
				}
			}

			.pagination {
				position: fixed;
				transition: left .2s ease;
				left: 300px;
				right: 0;
				bottom: 0;
				width: unset;
				height: fit-content;
				overflow: initial;
			}
		}
	}
}

@mixin condensed-table-style () {

	.search-button {
		position: relative;

		&:hover:not(.open) {

			> p {
				opacity: 1;
			}
		}

		> .icon {
			margin: 0 !important;
		}

		> p {
			opacity: 0;
			pointer-events: none;
			transition: opacity 0.2s ease;
			position: absolute;
			white-space: nowrap;
			left: 50%;
			top: calc(100% + 15px);
			transform: translate(-50%,0);
			padding: 10px;
			background:white;
			box-shadow: $defaultBoxShadow;
			border-radius: 5px;
			text-transform: uppercase;
			font-size: 12px !important;
			font-weight: 600;
			letter-spacing: 2px;
		}

		.dropdown-download {
			width: 100%;
			position: absolute !important;
			height: 100%;

			.dropdown-body {
				left: 50%;
				transform: translateX(-50%) !important;
				top: calc(100% + 12px);
				border-top: 3px solid $color-primary;
				overflow: visible !important;

				&::before {
					content: ' ';
					position: absolute;
					top: 0;
					left: 50%;
					transform: translate(-50%,calc(-50%) ) rotate(45deg);
					border: 8px solid $color-primary;
					border-bottom: 8px solid white;
					border-right: 8px solid white;
					z-index: 9;
				}

				.top {
					display: none !important;
				}

				.option {
					z-index: 10;
					position: relative;

					&:nth-last-child( 1 ) {
						border-bottom-right-radius: 10px;
						border-bottom-left-radius: 10px;
					}
				}
			}
		}
	}
}

@media ( max-width: 1230px ) {

	.space-table.nav-state-open {
		@include condensed-table-style();
	}
}

@media ( max-width: 1010px ) {

	.space-table {
		@include condensed-table-style();
	}
}

@media only screen and ( max-width: 640px ) {


	.asset-table {

		.asset-search {

			.buttons {
				display: none;
			}

			.search-input {

				input {
					width: 0;
				}
			}
		}

		table {
			margin-bottom: 128px !important;
		}
	}
}
</style>
